import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getResumeById, downloadResumePDF, generateATSAnalysis } from '../services/resumeService';
import '../styles/ResumePreview.css';

const ResumePreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [atsAnalysis, setAtsAnalysis] = useState(null);
  const [analyzingATS, setAnalyzingATS] = useState(false);
  const [showATSResults, setShowATSResults] = useState(false);

  useEffect(() => {
    fetchResume();
  }, [id]);

  const fetchResume = async () => {
    try {
      setLoading(true);
      const data = await getResumeById(id);
      setResume(data.resume);
      // Load existing ATS analysis if available
      if (data.resume.atsAnalysis && data.resume.atsAnalysis.atsScore) {
        setAtsAnalysis(data.resume.atsAnalysis);
        setShowATSResults(true);
      }
    } catch (error) {
      console.error('Error fetching resume:', error);
      alert('Failed to fetch resume');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await downloadResumePDF(id, resume.templateId || 'modern');
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${resume.personalInfo.firstName}_${resume.personalInfo.lastName}_Resume.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const handleGenerateATS = async () => {
    try {
      setAnalyzingATS(true);
      const analysis = await generateATSAnalysis(id);
      console.log('ATS Analysis received:', analysis);
      setAtsAnalysis(analysis);
      setShowATSResults(true);
    } catch (error) {
      console.error('Error generating ATS analysis:', error);
      alert('Failed to generate ATS analysis. Please try again.');
    } finally {
      setAnalyzingATS(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Present';
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  const getTemplateColors = () => {
    const templateColors = {
      modern: '#2563eb',
      bold: '#be123c',
      elegant: '#9333ea',
      professional: '#059669',
      executive: '#7c3aed',
      classic: '#4b5563',
      creative: '#dc2626',
      academic: '#ea580c',
      minimal: '#64748b',
      technical: '#0891b2'
    };
    const templateId = resume?.templateId || 'modern';
    return {
      primary: templateColors[templateId] || '#2563eb',
      secondary: '#64748b',
      text: '#1e293b'
    };
  };

  const renderModernTemplate = () => {
    const colors = getTemplateColors();
    return (
      <div className="template-modern" style={{ '--primary': colors.primary }}>
        <div className="modern-header">
          <h1>{personalInfo.firstName} {personalInfo.lastName}</h1>
          {resume.targetPosition && <div className="modern-title">{resume.targetPosition}</div>}
          <div className="modern-contact">
            <span>{personalInfo.email}</span>
            {personalInfo.phone && <><span>•</span><span>{personalInfo.phone}</span></>}
            {personalInfo.location?.city && <><span>•</span><span>{personalInfo.location.city}</span></>}
          </div>
        </div>
        {renderSections('modern')}
      </div>
    );
  };

  const renderProfessionalTemplate = () => {
    const colors = getTemplateColors();
    return (
      <div className="template-professional" style={{ '--primary': colors.primary }}>
        <div className="professional-sidebar">
          <div className="sidebar-section">
            <h1>{personalInfo.firstName}<br/>{personalInfo.lastName}</h1>
            <div className="sidebar-contact">
              <div>{personalInfo.email}</div>
              {personalInfo.phone && <div>{personalInfo.phone}</div>}
              {personalInfo.location?.city && <div>{personalInfo.location.city}</div>}
            </div>
          </div>
          {skills && (skills.technical?.length > 0 || skills.soft?.length > 0) && (
            <div className="sidebar-section">
              <h3>Skills</h3>
              {skills.technical?.map((skill, i) => (
                <div key={i} className="sidebar-skill">{skill}</div>
              ))}
            </div>
          )}
        </div>
        <div className="professional-main">
          {resume.targetPosition && <div className="professional-position">{resume.targetPosition}</div>}
          {renderSections('professional')}
        </div>
      </div>
    );
  };

  const renderCreativeTemplate = () => {
    const colors = getTemplateColors();
    return (
      <div className="template-creative" style={{ '--primary': colors.primary }}>
        <div className="creative-header">
          <div className="creative-name-section">
            <h1>{personalInfo.firstName}</h1>
            <h1 className="creative-lastname">{personalInfo.lastName}</h1>
          </div>
          {resume.targetPosition && <div className="creative-title">{resume.targetPosition}</div>}
        </div>
        <div className="creative-contact-bar">
          {personalInfo.email} | {personalInfo.phone} | {personalInfo.location?.city}
        </div>
        {renderSections('creative')}
      </div>
    );
  };

  const renderMinimalTemplate = () => {
    const colors = getTemplateColors();
    return (
      <div className="template-minimal" style={{ '--primary': colors.primary }}>
        <div className="minimal-header">
          <h1>{personalInfo.firstName} {personalInfo.lastName}</h1>
          <div className="minimal-contact">
            {personalInfo.email} • {personalInfo.phone} • {personalInfo.location?.city}
          </div>
        </div>
        {renderSections('minimal')}
      </div>
    );
  };

  const renderSections = (templateType) => {
    const { summary, experience, education, skills, projects, certifications, settings } = resume;
    
    return (
      <>
        {summary && settings?.sections?.summary !== false && (
          <div className={`${templateType}-section`}>
            <h2 className={`${templateType}-section-title`}>Summary</h2>
            <p className={`${templateType}-summary-text`}>{summary}</p>
          </div>
        )}

        {experience && experience.length > 0 && settings?.sections?.experience !== false && (
          <div className={`${templateType}-section`}>
            <h2 className={`${templateType}-section-title`}>Experience</h2>
            {experience.map((exp, index) => (
              <div key={index} className={`${templateType}-item`}>
                <div className={`${templateType}-item-header`}>
                  <div>
                    <h3 className={`${templateType}-item-title`}>{exp.jobTitle}</h3>
                    <div className={`${templateType}-item-subtitle`}>{exp.company}</div>
                  </div>
                  <div className={`${templateType}-item-date`}>
                    {formatDate(exp.startDate)} - {exp.currentlyWorking ? 'Present' : formatDate(exp.endDate)}
                  </div>
                </div>
                {exp.description && <p>{exp.description}</p>}
                {exp.achievements && exp.achievements.length > 0 && (
                  <ul>
                    {exp.achievements.filter(a => a).map((achievement, i) => (
                      <li key={i}>{achievement}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        {education && education.length > 0 && settings?.sections?.education !== false && (
          <div className={`${templateType}-section`}>
            <h2 className={`${templateType}-section-title`}>Education</h2>
            {education.map((edu, index) => (
              <div key={index} className={`${templateType}-item`}>
                <div className={`${templateType}-item-header`}>
                  <div>
                    <h3 className={`${templateType}-item-title`}>
                      {edu.degree}{edu.field ? ` in ${edu.field}` : ''}
                    </h3>
                    <div className={`${templateType}-item-subtitle`}>{edu.institution}</div>
                  </div>
                  <div className={`${templateType}-item-date`}>
                    {formatDate(edu.startDate)} - {edu.currentlyStudying ? 'Present' : formatDate(edu.endDate)}
                  </div>
                </div>
                {edu.gpa && <p>GPA: {edu.gpa}</p>}
              </div>
            ))}
          </div>
        )}

        {templateType !== 'professional' && skills && (skills.technical?.length > 0 || skills.soft?.length > 0) && (
          <div className={`${templateType}-section`}>
            <h2 className={`${templateType}-section-title`}>Skills</h2>
            <div className={`${templateType}-skills`}>
              {skills.technical?.map((skill, i) => (
                <span key={i} className={`${templateType}-skill-tag`}>{skill}</span>
              ))}
            </div>
          </div>
        )}

        {projects && projects.length > 0 && settings?.sections?.projects !== false && (
          <div className={`${templateType}-section`}>
            <h2 className={`${templateType}-section-title`}>Projects</h2>
            {projects.map((proj, index) => (
              <div key={index} className={`${templateType}-item`}>
                <div className={`${templateType}-item-header`}>
                  <div>
                    <h3 className={`${templateType}-item-title`}>{proj.name}</h3>
                    {proj.role && <div className={`${templateType}-item-subtitle`}>{proj.role}</div>}
                  </div>
                  {proj.startDate && (
                    <div className={`${templateType}-item-date`}>
                      {formatDate(proj.startDate)} - {proj.currentlyWorking ? 'Present' : formatDate(proj.endDate)}
                    </div>
                  )}
                </div>
                {proj.description && <p>{proj.description}</p>}
              </div>
            ))}
          </div>
        )}

        {certifications && certifications.length > 0 && settings?.sections?.certifications !== false && (
          <div className={`${templateType}-section`}>
            <h2 className={`${templateType}-section-title`}>Certifications</h2>
            {certifications.map((cert, index) => (
              <div key={index} className={`${templateType}-item`}>
                <div className={`${templateType}-item-header`}>
                  <div>
                    <h3 className={`${templateType}-item-title`}>{cert.name}</h3>
                    <div className={`${templateType}-item-subtitle`}>{cert.issuer}</div>
                  </div>
                  <div className={`${templateType}-item-date`}>{formatDate(cert.issueDate)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  const renderTemplate = () => {
    const templateId = resume?.templateId || 'modern';
    switch (templateId) {
      case 'modern':
      case 'bold':
      case 'elegant':
        return renderModernTemplate();
      case 'professional':
      case 'executive':
      case 'classic':
        return renderProfessionalTemplate();
      case 'creative':
      case 'academic':
        return renderCreativeTemplate();
      case 'minimal':
      case 'technical':
        return renderMinimalTemplate();
      default:
        return renderModernTemplate();
    }
  };

  if (loading) {
    return <div className="preview-loading">Loading preview...</div>;
  }

  if (!resume) {
    return <div className="preview-error">Resume not found</div>;
  }

  const { personalInfo, summary, experience, education, skills, projects, certifications, settings } = resume;

  return (
    <div className="preview-container">
      <div className="preview-header">
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>📄 Resume Preview</h2>
          <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
            {resume.title} • {resume.targetPosition || 'No position specified'}
          </p>
        </div>
        <div className="header-actions">
          <button className="btn-back" onClick={() => navigate('/resumes')}>
            ← Back to Resumes
          </button>
          <button 
            className="btn-edit"
            onClick={() => navigate(`/resumes/edit/${id}`)}
          >
            ✏️ Edit
          </button>
          <button 
            className="btn-ats"
            onClick={handleGenerateATS}
            disabled={analyzingATS}
          >
            {analyzingATS ? '⏳ Analyzing...' : (atsAnalysis ? '🔄 Regenerate ATS Score' : '📊 Generate ATS Score')}
          </button>
          <button 
            className="btn-download"
            onClick={handleDownload}
          >
            ⬇️ Download PDF
          </button>
        </div>
      </div>
      
      {showATSResults && atsAnalysis && (
        <div className="ats-results-container">
          <div className="ats-results-header">
            <div>
              <h2>ATS Analysis Results</h2>
              {atsAnalysis.analyzedAt && (
                <p className="analyzed-date">Analyzed on {new Date(atsAnalysis.analyzedAt).toLocaleString()}</p>
              )}
            </div>
            <button className="close-ats" onClick={() => setShowATSResults(false)}>✕</button>
          </div>
          
          <div className="ats-score-section">
            <div className="ats-score-circle">
              <svg viewBox="0 0 120 120" className="score-svg">
                <circle cx="60" cy="60" r="54" fill="none" stroke="#e5e7eb" strokeWidth="8"/>
                <circle 
                  cx="60" 
                  cy="60" 
                  r="54" 
                  fill="none" 
                  stroke={atsAnalysis.atsScore >= 80 ? '#10b981' : atsAnalysis.atsScore >= 60 ? '#f59e0b' : '#ef4444'}
                  strokeWidth="8"
                  strokeDasharray={`${atsAnalysis.atsScore * 3.39} 339.292`}
                  strokeLinecap="round"
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div className="score-text">
                <div className="score-value">{atsAnalysis.atsScore}%</div>
                <div className="score-label">ATS Score</div>
              </div>
            </div>
            
            <div className="score-breakdown">
              <h3>Score Breakdown</h3>
              {atsAnalysis.scoreBreakdown && Object.entries(atsAnalysis.scoreBreakdown).map(([key, value]) => (
                <div key={key} className="breakdown-item">
                  <div className="breakdown-label">{key.charAt(0).toUpperCase() + key.slice(1)}</div>
                  <div className="breakdown-bar">
                    <div 
                      className="breakdown-fill" 
                      style={{ 
                        width: `${value}%`,
                        backgroundColor: value >= 80 ? '#10b981' : value >= 60 ? '#f59e0b' : '#ef4444'
                      }}
                    />
                  </div>
                  <div className="breakdown-value">{value}%</div>
                </div>
              ))}
            </div>
          </div>

          <div className="ats-sections">
            <div className="ats-section strengths">
              <h3>✓ Strengths</h3>
              <ul>
                {atsAnalysis.strengths?.map((strength, index) => (
                  <li key={index}>{strength}</li>
                ))}
              </ul>
            </div>

            <div className="ats-section weaknesses">
              <h3>⚠ Areas for Improvement</h3>
              <ul>
                {atsAnalysis.weaknesses?.map((weakness, index) => (
                  <li key={index}>{weakness}</li>
                ))}
              </ul>
            </div>

            <div className="ats-section recommendations">
              <h3>💡 Recommendations</h3>
              <ul>
                {atsAnalysis.recommendations?.map((rec, index) => (
                  <li key={index}>{rec}</li>
                ))}
              </ul>
            </div>

            {atsAnalysis.keywordSuggestions && atsAnalysis.keywordSuggestions.length > 0 && (
              <div className="ats-section keywords">
                <h3>🔑 Suggested Keywords</h3>
                <div className="keyword-tags">
                  {atsAnalysis.keywordSuggestions.map((keyword, index) => (
                    <span key={index} className="keyword-tag">{keyword}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="ats-section feedback">
              <h3>📝 Overall Feedback</h3>
              <p>{atsAnalysis.overallFeedback}</p>
            </div>
          </div>
        </div>
      )}

      <div className="resume-preview-wrapper">
        <div className="resume-preview">
          {renderTemplate()}
        </div>
      </div>
    </div>
  );
};

export default ResumePreview;
